import { ReactNode, useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";

import { CompanyUserCombobox } from "components/ui/form_components/CompanyUserCombobox";
import { EventNames, useTracking } from "lib/tracking";
import { CustomOrganisationPrimaryRole } from "lib/types/graphQLEnums";
import { useCreateCustomLeadOrganisation } from "../../../lib/hooks/api/leads/useCreateCustomLeadOrganisation";
import { LeadDetails } from "../../../lib/hooks/api/leads/useLead";
import { useRelateBidToLead } from "../../../lib/hooks/api/leads/useRelateBidToLead";
import { useFormAutoSubmit } from "../../../lib/hooks/useFormAutoSubmit";
import { Flex, Text } from "../../../styles/utility-components";
import Button from "../../ui/button/Button";
import { BuyersCombobox } from "../../ui/form_components/BidEntityCombobox/BuyersCombobox";
import { FrameworkCombobox } from "../../ui/form_components/BidEntityCombobox/FrameworkCombobox";
import { RelatedBidCombobox } from "../../ui/form_components/BidEntityCombobox/RelatedBidCombobox";
import { SuppliersCombobox } from "../../ui/form_components/BidEntityCombobox/SuppliersCombobox";
import { LeadStageSelect } from "../../ui/form_components/StageSelect/LeadStageSelect";
import DateInput from "../../ui/input/DateInput";
import InputNumber from "../../ui/input/InputNumber";
import toast from "../../ui/toast/Toast";
import {
  LeadUpdateFormInput,
  LeadUpdateFormOutput,
  updateLeadSchema,
  useLeadUpdateFormSubmit,
} from "./useLeadUpdateFormSubmit";

type Props = {
  lead: LeadDetails;
};

export default function LeadDetailsTab({ lead }: Props) {
  const { logEvent } = useTracking();
  const navigate = useNavigate();

  const form = useForm<LeadUpdateFormInput, unknown, LeadUpdateFormOutput>({
    defaultValues: {
      name: lead.name,
      value: lead.value,
      description: lead.description,
      assignedToId: lead.assignedTo?.guid,
      stageId: lead.stage.id,
      expiryDate: lead.expiryDate,
      buyerIds: [...lead.buyers.map((b) => b.id), ...lead.customBuyers.map((b) => b.id)],
      supplierIds: lead.suppliers.map((s) => s.id),
      frameworkId: lead.frameworks.length > 0 ? lead.frameworks[0].id : null,
      bidId: lead.bid?.id ?? null,
    },
    resolver: zodResolver(updateLeadSchema),
    mode: "onChange",
  });

  const { debouncedHandleSubmit, submitValueUpdate, reset } = useLeadUpdateFormSubmit({
    lead,
    form,
  });

  const { mutate: createCustomOrganisation } = useCreateCustomLeadOrganisation({
    onSuccess: () => {
      toast.success(`Custom organisation saved to ${lead.name}`);
      logEvent(EventNames.leadDataAdded, {
        "Data type": "BUYER",
        "Lead ID": lead.id,
        "Free-text buyer": true,
        "Amount of entities": 1,
      });
    },
    onError: (error) => {
      console.warn("Failed to create custom organisation on lead", JSON.stringify(error));
      toast.error("Failed to create custom organisation");
    },
  });

  // Different to the relateToBid mutation in useLeadUpdateFormSubmit as we navigate to the created bid here
  const { mutate: relateBidToLeadOnCreation } = useRelateBidToLead({
    onSuccess: (data) => {
      if (data.relateBidToLead.bid) {
        void navigate({ to: "/bids/$id", params: { id: data.relateBidToLead.bid.id } });
        reset({}, { keepValues: true });
      }
    },
    onError: (error) => {
      console.warn("Failed to create bid on lead", JSON.stringify(error));
      toast.error("Failed to create bid on lead");
    },
  });

  const { control, watch, getValues, trigger, setValue } = form;

  // Keep entity field values in sync with lead data from the server.
  // This handles cases like the UNDO toast re-adding an entity, which updates
  // `lead` via query invalidation but does not update the form's controlled value.
  useEffect(() => {
    setValue("buyerIds", [...lead.buyers.map((b) => b.id), ...lead.customBuyers.map((b) => b.id)]);
  }, [lead.buyers, lead.customBuyers, setValue]);

  useEffect(() => {
    setValue(
      "supplierIds",
      lead.suppliers.map((s) => s.id),
    );
  }, [lead.suppliers, setValue]);

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: debouncedHandleSubmit,
    provideMetadata: true,
  });

  const {
    field: { onChange: updateBid },
  } = useController({
    control,
    name: "bidId",
  });

  return (
    <Form aria-label="Lead details sidebar">
      {/* Details Section */}
      <Section>
        <Text h3>Details</Text>
        <Property aria-label="Stage field">
          <FieldLabel>Stage</FieldLabel>
          <FieldValue>
            <LeadStageSelect control={control} name="stageId" aria-label="Stage search" />
          </FieldValue>
        </Property>

        <Property aria-label="Owner field">
          <FieldLabel>Owner</FieldLabel>
          <FieldValue>
            <CompanyUserCombobox control={control} name="assignedToId" placeholder="Select user" />
          </FieldValue>
        </Property>

        <Property aria-label="Buyer field">
          <FieldLabel>Buyer</FieldLabel>
          <FieldValue>
            <BuyersCombobox
              control={control}
              name="buyerIds"
              customBuyers={lead.customBuyers}
              onAddCustomBuyer={(name: string) =>
                createCustomOrganisation({
                  leadId: lead.id,
                  input: { name, primaryRole: CustomOrganisationPrimaryRole.Buyer },
                })
              }
            />
          </FieldValue>
        </Property>

        <Property aria-label="Supplier field">
          <FieldLabel>Supplier</FieldLabel>
          <FieldValue>
            <SuppliersCombobox control={control} name="supplierIds" />
          </FieldValue>
        </Property>

        <Property aria-label="Value field">
          <FieldLabel>Value</FieldLabel>
          <InlineSelectWrapper
            displayValue={
              lead.value != null ? (
                <FieldTextValue>£{lead.value.toLocaleString()}</FieldTextValue>
              ) : undefined
            }
          >
            <InputNumber
              name="value"
              autoFocus // Input only renders when you click edit + it's needed to ensure onBlur triggers on click away
              control={control}
              placeholder="£100,000"
              onBlur={submitValueUpdate}
              onKeyDown={(e) => {
                // Enter commits the value: blur triggers save + exits edit mode
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
          </InlineSelectWrapper>
        </Property>

        <Property aria-label="Framework field">
          <FieldLabel>Framework</FieldLabel>
          <FieldValue>
            <FrameworkCombobox name="frameworkId" control={control} />
          </FieldValue>
        </Property>
      </Section>

      {/* Key dates Section */}

      <Section>
        <Text h3>Key dates</Text>
        <Property aria-label="Award expiry date field">
          <FieldLabel>Award expiry date</FieldLabel>
          <DateInput
            name="expiryDate"
            control={control}
            calendarProps={{ mode: "single" }}
            triggerWidth="100%"
          />
        </Property>
      </Section>

      {/* Related bid Section */}

      <Section>
        <Text h3>Related bid</Text>
        <Property aria-label="Related Bid field">
          <FieldLabel>Related Bid</FieldLabel>
          <RelatedBidCombobox
            control={control}
            name="bidId"
            defaultBidRequest={{
              assignedToLead: false,
            }}
            selectedBid={lead.bid}
            selectedLead={lead}
            onChange={(val) => updateBid(val ?? null)}
            onCreateBid={(bidId) =>
              relateBidToLeadOnCreation({
                req: { leadId: lead.id, bidId },
              })
            }
          />
        </Property>
      </Section>
    </Form>
  );
}

type InlineSelectWrapperProps = {
  children: ReactNode;
  displayValue: ReactNode | undefined;
};

function InlineSelectWrapper({ children, displayValue }: InlineSelectWrapperProps) {
  const [isEditing, setIsEditing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  const clearBlurTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  const handleBlur = (e: React.FocusEvent) => {
    clearBlurTimeout();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      timeoutRef.current = setTimeout(() => {
        setIsEditing(false);
        timeoutRef.current = null;
      }, 500);
    }
  };
  const handleFocus = () => {
    clearBlurTimeout();
  };

  if (isEditing) {
    return (
      <FieldValue onBlur={handleBlur} onFocus={handleFocus}>
        {children}
      </FieldValue>
    );
  }

  if (displayValue) {
    return (
      <DisplayContainer aria-label="Edit field" onClick={() => setIsEditing(true)}>
        {displayValue}
      </DisplayContainer>
    );
  }

  return (
    <ClickToAddValueContainer>
      <Button
        buttonStyle="inverse"
        iconProps={{ color: "sysTextPlaceholder" }}
        buttonType="primary"
        icon="plus"
        onClick={() => setIsEditing(true)}
        fullWidth
        justifyContent="flex-start"
      >
        <Text color="sysTextPlaceholder">Click to add</Text>
      </Button>
    </ClickToAddValueContainer>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 48,
  padding: 24,
});

const Section = styled(Flex)({
  flexDirection: "column",
  gap: 16,
});

const Property = styled(Flex)({
  alignItems: "center",
  gap: 16,
});

const FieldLabel = styled.label(({ theme }) => ({
  minWidth: 130,
  color: theme.colors.sysTextSecondary,
  flexShrink: 0,
  alignSelf: "flex-start",
  padding: "5px 0",
}));

const FieldValue = styled.div({
  flex: 1,
  minWidth: 0,
});
// Different from above because it's flex
const ClickToAddValueContainer = styled(Flex)({
  flex: 1,
  minWidth: 250,
});

const FieldTextValue = styled(Text)({
  maxWidth: 200,
});

const DisplayContainer = styled(Flex)(({ theme }) => ({
  flexWrap: "wrap",
  alignItems: "center",
  flex: 1,
  gap: 8,
  padding: "5px 12px", // 12px left aligns the resting value with the date input below
  borderRadius: 4,
  cursor: "pointer",
  maxWidth: 300,
  "&:hover": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
}));
